import React, { useState, useEffect } from 'react';
import { MessageCircle, Clock, CheckCircle, AlertCircle, Send, Eye, X, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import Layout from '../components/Layout';
import AdminNav from '../components/AdminNav';
import { auth } from '../firebase/config';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Create authenticated axios instance
const createAuthClient = async () => {
  const token = await auth.currentUser?.getIdToken();
  return axios.create({
    baseURL: API_URL,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
};

const AdminChatSupport = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshingTicket, setRefreshingTicket] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check if user is admin
  const adminEmails = ['sagarshiva111555@gmail.com', 'allgrowtechonologies@gmail.com'];
  const isAdmin = user && adminEmails.includes(user.email);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    if (!isAdmin) {
      toast({
        title: "Access Denied",
        description: "Admin access required",
        variant: "destructive"
      });
      navigate('/dashboard');
      return;
    }

    loadData();
  }, [user, isAdmin, statusFilter]);

  // Auto-refresh tickets list every 10 seconds
  useEffect(() => {
    if (!isAdmin || !autoRefresh) return;

    const interval = setInterval(() => {
      loadData();
      // Also refresh selected ticket if one is open
      if (selectedTicket) {
        loadTicketDetails(selectedTicket, true); // Silent refresh
      }
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [isAdmin, autoRefresh, selectedTicket]);

  const loadData = async () => {
    setLoading(true);
    try {
      const apiClient = await createAuthClient();
      
      const [ticketsResponse, statsResponse] = await Promise.all([
        apiClient.get('/api/admin/chat-support/tickets', {
          params: { status_filter: statusFilter || undefined }
        }),
        apiClient.get('/api/admin/chat-support/stats')
      ]);
      
      console.log('✅ Tickets loaded:', ticketsResponse.data.length);
      setTickets(ticketsResponse.data);
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to load support data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadTicketDetails = async (ticketId, silent = false) => {
    if (!silent) {
      setRefreshingTicket(true);
    }
    
    try {
      const apiClient = await createAuthClient();
      const response = await apiClient.get(`/api/admin/chat-support/tickets/${ticketId}`);
      
      if (!silent) {
        console.log('✅ Ticket details loaded:', response.data);
      }
      
      setTicketDetails(response.data);
      
      // Only set selectedTicket if not a background refresh
      if (!silent) {
        setSelectedTicket(ticketId);
      }
    } catch (error) {
      console.error('Error loading ticket details:', error);
      if (!silent) {
        toast({
          title: "Error",
          description: error.response?.data?.detail || "Failed to load ticket details",
          variant: "destructive"
        });
      }
    } finally {
      if (!silent) {
        setRefreshingTicket(false);
      }
    }
  };

  const sendResponse = async () => {
    if (!responseMessage.trim()) {
      toast({
        title: "Error",
        description: "Please enter a response message",
        variant: "destructive"
      });
      return;
    }

    setSending(true);
    try {
      const apiClient = await createAuthClient();
      await apiClient.post(`/api/admin/chat-support/tickets/${selectedTicket}/respond`, {
        ticket_id: selectedTicket,
        response_message: responseMessage,
        mark_as_resolved: false
      });

      toast({
        title: "Success",
        description: "Your message has been sent to the user's chat"
      });

      setResponseMessage('');
      loadTicketDetails(selectedTicket);
      loadData();
    } catch (error) {
      console.error('Error sending response:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send response",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const markAsResolved = async () => {
    if (!window.confirm('Mark this conversation as resolved?')) {
      return;
    }

    try {
      const apiClient = await createAuthClient();
      await apiClient.patch(`/api/admin/chat-support/tickets/${selectedTicket}/mark-resolved`);

      toast({
        title: "Success",
        description: "Conversation marked as resolved"
      });

      setSelectedTicket(null);
      loadData();
    } catch (error) {
      console.error('Error marking as resolved:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to mark as resolved",
        variant: "destructive"
      });
    }
  };

  const deleteChat = async () => {
    if (!window.confirm('Are you sure you want to delete this entire conversation? This action cannot be undone.')) {
      return;
    }

    try {
      const apiClient = await createAuthClient();
      await apiClient.delete(`/api/admin/chat-support/tickets/${selectedTicket}`);

      toast({
        title: "Success",
        description: "Conversation deleted successfully"
      });

      setSelectedTicket(null);
      loadData();
    } catch (error) {
      console.error('Error deleting chat:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete conversation",
        variant: "destructive"
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'resolved': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <AlertCircle className="w-4 h-4" />;
      case 'resolved': return <CheckCircle className="w-4 h-4" />;
      default: return <MessageCircle className="w-4 h-4" />;
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <Layout>
      <AdminNav />
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <MessageCircle className="w-8 h-8 text-amber-600" />
                Admin Chat Support
              </h1>
              <p className="text-gray-600 mt-2">View and respond to all chatbot conversations</p>
            </div>
            
            {/* Auto-refresh toggle */}
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-md">
              <span className="text-sm text-gray-700">Auto-refresh (10s)</span>
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  autoRefresh ? 'bg-amber-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    autoRefresh ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-yellow-500">
                <p className="text-sm text-gray-600">Pending Response</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.tickets.pending}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-green-500">
                <p className="text-sm text-gray-600">Responded</p>
                <p className="text-2xl font-bold text-green-600">{stats.tickets.resolved}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-amber-500">
                <p className="text-sm text-gray-600">Total Chats</p>
                <p className="text-2xl font-bold text-amber-600">{stats.tickets.total}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-blue-500">
                <p className="text-sm text-gray-600">Recent (7 Days)</p>
                <p className="text-2xl font-bold text-blue-600">{stats.recent_tickets_7_days}</p>
              </div>
            </div>
          )}

          {/* Filters and Refresh */}
          <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
            <div className="flex gap-2">
              <button
                onClick={() => setStatusFilter('')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  statusFilter === '' ? 'bg-amber-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setStatusFilter('pending')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  statusFilter === 'pending' ? 'bg-yellow-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                Pending
              </button>
              <button
                onClick={() => setStatusFilter('resolved')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  statusFilter === 'resolved' ? 'bg-green-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                Responded
              </button>
            </div>
            <button
              onClick={loadData}
              className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>

          {/* Content Area */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Tickets Column */}
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900">Chat Conversations</h2>
            
            {loading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-amber-600" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">No chat conversations found</p>
                <p className="text-sm text-gray-500 mt-2">Conversations will appear here when users interact with the chatbot</p>
              </div>
            ) : (
              tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className={`bg-white rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow ${
                    selectedTicket === ticket.id ? 'ring-2 ring-amber-500' : ''
                  }`}
                  onClick={() => loadTicketDetails(ticket.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border flex items-center gap-1 ${getStatusColor(ticket.status)}`}>
                          {getStatusIcon(ticket.status)}
                          {ticket.status === 'pending' ? 'NEEDS RESPONSE' : 'RESPONDED'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {ticket.message_count} messages
                        </span>
                      </div>
                      <p className="font-semibold text-gray-900 mb-1">
                        {ticket.user_name || ticket.user_email || 'Guest User'}
                      </p>
                      {ticket.phone_number && (
                        <p className="text-sm text-blue-600 mb-1 flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                          </svg>
                          {ticket.phone_number}
                        </p>
                      )}
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                        {ticket.message}
                      </p>
                      <p className="text-xs text-gray-500">
                        Started: {new Date(ticket.created_at).toLocaleString()}
                      </p>
                    </div>
                    <Eye className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Ticket Details Column */}
          <div className="lg:sticky lg:top-4">
            {!selectedTicket ? (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">Select a conversation to view details and respond</p>
              </div>
            ) : ticketDetails ? (
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-amber-600 to-yellow-500 text-white p-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-lg">Chat Conversation</h3>
                    <p className="text-sm text-amber-100">{ticketDetails.ticket.user_email}</p>
                    <p className="text-xs text-amber-200">{ticketDetails.chat_history.length} messages</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => loadTicketDetails(selectedTicket)}
                      disabled={refreshingTicket}
                      className="hover:bg-white/20 p-2 rounded transition-colors disabled:opacity-50"
                      title="Refresh conversation"
                    >
                      <RefreshCw className={`w-5 h-5 ${refreshingTicket ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                      onClick={() => setSelectedTicket(null)}
                      className="hover:bg-white/20 p-1 rounded transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* User Info & Navigation */}
                {ticketDetails.chat_history.length > 0 && (
                  <div className="p-4 bg-blue-50 border-b">
                    <div className="grid grid-cols-2 gap-4 mb-2">
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-1">User Name</p>
                        <p className="text-sm font-medium text-gray-900">
                          {ticketDetails.chat_history[0].user_name || 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-1">Phone Number</p>
                        <p className="text-sm font-medium text-gray-900">
                          {ticketDetails.chat_history.find(msg => msg.phone_number)?.phone_number || 'Not provided'}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-2">
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-1">User Email</p>
                        <p className="text-sm font-medium text-gray-900">
                          {ticketDetails.ticket.user_email || 'Guest (No email)'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-1">User Role</p>
                        <p className="text-sm font-medium text-gray-900 capitalize">
                          {ticketDetails.ticket.user_role}
                        </p>
                      </div>
                    </div>
                    
                    {/* Navigation History */}
                    {ticketDetails.chat_history.some(msg => msg.navigation_history && msg.navigation_history.length > 0) && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold text-gray-600 mb-2">Navigation History (Pages Visited)</p>
                        <div className="flex flex-wrap gap-1">
                          {(() => {
                            // Get the most recent navigation history
                            const msgWithNav = ticketDetails.chat_history.reverse().find(m => m.navigation_history && m.navigation_history.length > 0);
                            ticketDetails.chat_history.reverse(); // Reverse back
                            const navHistory = msgWithNav?.navigation_history || [];
                            
                            return navHistory.map((page, idx) => (
                              <span 
                                key={idx}
                                className="inline-block px-2 py-1 bg-white text-xs rounded border border-blue-200 text-gray-700"
                              >
                                {page}
                              </span>
                            ));
                          })()}
                        </div>
                      </div>
                    )}
                    
                    {/* Current Page */}
                    {ticketDetails.chat_history.some(msg => msg.current_page) && (
                      <div className="mt-2">
                        <p className="text-xs font-semibold text-gray-600">Last Active Page</p>
                        <p className="text-sm text-gray-900 mt-1">
                          {ticketDetails.chat_history[ticketDetails.chat_history.length - 1].current_page || 'Unknown'}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Chat History */}
                <div className="p-4 max-h-96 overflow-y-auto bg-gray-50">
                  <h4 className="font-semibold text-gray-900 mb-3">Conversation History</h4>
                  <div className="space-y-3">
                    {ticketDetails.chat_history.map((msg, index) => (
                      <div key={index}>
                        {msg.user_message && (
                          <div className="flex justify-start mb-2">
                            <div className="bg-blue-100 text-blue-900 rounded-lg px-3 py-2 max-w-[80%]">
                              <p className="text-xs font-semibold mb-1">User:</p>
                              <p className="text-sm">{msg.user_message}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(msg.timestamp).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        )}
                        {msg.bot_response && (
                          <div className="flex justify-end mb-2">
                            <div className={`rounded-lg px-3 py-2 max-w-[80%] ${
                              msg.is_admin_message 
                                ? 'bg-green-100 text-green-900' 
                                : 'bg-gray-200 text-gray-900'
                            }`}>
                              <p className="text-xs font-semibold mb-1">
                                {msg.is_admin_message ? `Human (${msg.admin_name}):` : 'AI Bot:'}
                              </p>
                              <p className="text-sm whitespace-pre-wrap">{msg.bot_response}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Admin Responses Section */}
                {ticketDetails.admin_responses.length > 0 && (
                  <div className="p-4 bg-green-50 border-t">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Your Responses
                    </h4>
                    {ticketDetails.admin_responses.map((response) => (
                      <div key={response.id} className="bg-white rounded-lg p-3 mb-2">
                        <p className="text-sm font-semibold text-amber-600">{response.admin_email}</p>
                        <p className="text-sm text-gray-700 mt-1">{response.message}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(response.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Response Form */}
                <div className="p-4 border-t">
                  <h4 className="font-semibold text-gray-900 mb-2">Send Message to User</h4>
                  <p className="text-xs text-gray-600 mb-3">
                    Your message will appear in the user&apos;s chatbot as a human support response
                  </p>
                  <textarea
                    value={responseMessage}
                    onChange={(e) => setResponseMessage(e.target.value)}
                    placeholder="Type your message to the user..."
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    rows="4"
                  />
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={sendResponse}
                      disabled={sending}
                      className="flex-1 bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white py-2 px-4 rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <Send className="w-4 h-4" />
                      {sending ? 'Sending...' : 'Send to User\'s Chat'}
                    </button>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="p-4 bg-gray-50 border-t">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Actions:</p>
                  <div className="flex gap-2">
                    <button
                      onClick={markAsResolved}
                      className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Mark as Resolved
                    </button>
                    <button
                      onClick={deleteChat}
                      className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Delete Chat
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Mark as resolved to close the ticket, or delete to permanently remove this conversation
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-amber-600" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </Layout>
  );
};

export default AdminChatSupport;
